import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "../../../components/icons";
import {
  confirmPayment,
  downloadInvoicePdf,
  getAodsForCase,
  getInvoiceDetails,
  getInvoicePayments,
  recordPayment,
  rejectPayment,
  requestAod,
} from "../../../services/revenue";
import { useToast } from "../../../hooks/useToast";
import { OperationsConfirmDialog, ifasDisabledBtnSx, moneyCellSx } from "../ifas/components/OperationsUI";
import { formatR } from "../../../utils/money";

const fmtDate = (d?: string) => (d ? String(d).slice(0, 10) : "-");

const STATUS: Record<string, { bg: string; fg: string }> = {
  ISSUED: { bg: "#F3F4F6", fg: "#374151" },
  PAYMENT_PENDING: { bg: "#FBF1DC", fg: "#9A6A00" },
  PARTIALLY_PAID: { bg: "#EEF2FF", fg: "#3730A3" },
  PAID: { bg: "#E6F4EC", fg: "#1A7F4B" },
  OVERDUE: { bg: "#FBE8E4", fg: "#B3341F" },
  UPLOADED: { bg: "#FBF1DC", fg: "#9A6A00" },
  CONFIRMED: { bg: "#E6F4EC", fg: "#1A7F4B" },
  REJECTED: { bg: "#FBE8E4", fg: "#B3341F" },
};

function StatusChip({ status }: { status?: string }) {
  const c = STATUS[status || ""] || { bg: "#F3F4F6", fg: "#4B5563" };
  return <Chip size="small" label={String(status || "-").replace(/_/g, " ")} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700 }} />;
}

interface Props {
  invoiceNumber: string | null;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export default function InvoiceDetailDialog({ invoiceNumber, open, onClose, onChanged }: Props) {
  const { showInfoToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [invoice, setInvoice] = useState<any>(null);
  const [payments, setPayments] = useState<any[]>([]);

  // PoP capture form
  const [amount, setAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [reference, setReference] = useState("");
  const [cashAccountId, setCashAccountId] = useState("");
  const [file, setFile] = useState<File | null>(null);

  // Acknowledgement of Debt (SOP §12.1, FR-094/095)
  const [aods, setAods] = useState<any[]>([]);
  const [aodOpen, setAodOpen] = useState(false);
  const [aodReason, setAodReason] = useState("");
  const [aodConfirmOpen, setAodConfirmOpen] = useState(false);
  // Key of the single in-flight action, so only the clicked button spins.
  const [pending, setPending] = useState<string | null>(null);
  const spinner = (key: string) => (pending === key ? <CircularProgress size={14} color="inherit" /> : undefined);

  const load = useCallback(async () => {
    if (!invoiceNumber) return;
    setLoading(true);
    const [inv, pays] = await Promise.all([getInvoiceDetails(invoiceNumber), getInvoicePayments(invoiceNumber)]);
    if (inv?.status === 200) {
      setInvoice(inv.data);
      if (inv.data?.caseNumber) {
        const aodRes = await getAodsForCase(inv.data.caseNumber);
        setAods(aodRes?.status === 200 && Array.isArray(aodRes.data) ? aodRes.data : []);
      } else {
        setAods([]);
      }
    }
    if (pays?.status === 200) setPayments(Array.isArray(pays.data) ? pays.data : []);
    setLoading(false);
  }, [invoiceNumber]);

  // Validates first, then opens the confirmation gate — an AoD starts a
  // GCOO + GCEO approval chain, so it should never fire on a single stray click.
  const askAodConfirmation = () => {
    if (!invoice?.caseNumber) {
      showInfoToast("This invoice has no linked case, so an AoD cannot be raised.");
      return;
    }
    if (!aodReason.trim()) {
      showInfoToast("A reason is required — it is the audit justification for releasing without payment.");
      return;
    }
    setAodConfirmOpen(true);
  };

  const submitAod = async () => {
    if (busy) return;
    if (!invoice?.caseNumber || !aodReason.trim()) return;
    setAodConfirmOpen(false);
    setBusy(true);
    setPending("aod");
    const res = await requestAod({
      caseNumber: invoice.caseNumber,
      invoiceNumber: invoice.invoiceNumber,
      amount: invoice.outstandingAmount,
      reason: aodReason.trim(),
    });
    setBusy(false);
    setPending(null);
    if (res?.status === 201 || res?.status === 200) {
      showInfoToast(`AoD ${res.data?.aodNumber} raised — awaiting GCOO and GCEO approval.`);
      setAodOpen(false);
      setAodReason("");
      await load();
      // refresh the parent so the new request appears on the AoD approvals tab
      onChanged();
    } else {
      showInfoToast(res?.data?.message || "Could not raise the AoD request.");
    }
  };

  useEffect(() => {
    if (open && invoiceNumber) {
      setAmount(""); setPaidDate(""); setReference(""); setCashAccountId(""); setFile(null);
      load();
    }
  }, [open, invoiceNumber, load]);

  const handleDownloadPdf = async () => {
    if (!invoiceNumber) return;
    const res = await downloadInvoicePdf(invoiceNumber);
    if (res?.status === 200) {
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } else {
      showInfoToast("Could not download the invoice PDF.");
    }
  };

  const handleRecord = async () => {
    if (!invoiceNumber) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      showInfoToast("Enter a payment amount greater than zero.");
      return;
    }
    if (busy) return;
    setBusy(true);
    setPending("record");
    const res = await recordPayment(
      invoiceNumber,
      { amount: amt, paidDate: paidDate || undefined, reference: reference || undefined, cashAccountId: cashAccountId || undefined },
      file,
    );
    setBusy(false);
    setPending(null);
    if (res?.status === 201 || res?.status === 200) {
      showInfoToast("Proof of payment recorded — awaiting confirmation.");
      setAmount(""); setReference(""); setPaidDate(""); setCashAccountId(""); setFile(null);
      await load();
      onChanged();
    } else {
      showInfoToast(res?.data?.message || "Could not record the payment.");
    }
  };

  const handleConfirm = async (paymentId: string) => {
    if (busy) return;
    setBusy(true);
    setPending(`confirm:${paymentId}`);
    const res = await confirmPayment(paymentId);
    setBusy(false);
    setPending(null);
    if (res?.status === 200) {
      showInfoToast("Payment confirmed against the bank statement.");
      await load();
      onChanged();
    } else {
      showInfoToast(res?.data?.message || "Could not confirm the payment.");
    }
  };

  const handleReject = async (paymentId: string) => {
    if (busy) return;
    setBusy(true);
    setPending(`reject:${paymentId}`);
    const res = await rejectPayment(paymentId, "Rejected by finance");
    setBusy(false);
    setPending(null);
    if (res?.status === 200) {
      showInfoToast("Payment rejected.");
      await load();
      onChanged();
    } else {
      showInfoToast(res?.data?.message || "Could not reject the payment.");
    }
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Invoice {invoiceNumber}</span>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StatusChip status={invoice?.status} />
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading || !invoice ? (
          <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} /></Box>
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mb: 2 }}>
              <Box><Typography fontSize={11} color="#7b8794">BILL TO</Typography><Typography fontWeight={700}>{invoice.clientName || "-"}</Typography></Box>
              <Box><Typography fontSize={11} color="#7b8794">CASE</Typography><Typography fontWeight={700}>{invoice.caseNumber || "-"}</Typography></Box>
              <Box><Typography fontSize={11} color="#7b8794">ISSUE DATE</Typography><Typography fontWeight={700}>{fmtDate(invoice.issueDate)}</Typography></Box>
              <Box><Typography fontSize={11} color="#7b8794">DUE DATE</Typography><Typography fontWeight={700}>{fmtDate(invoice.dueDate)}</Typography></Box>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Unit</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">VAT</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(invoice.lineItems ?? []).map((li: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{li.description}</TableCell>
                    <TableCell sx={moneyCellSx}>{li.quantity}</TableCell>
                    <TableCell sx={moneyCellSx}>{formatR(li.unitPrice)}</TableCell>
                    <TableCell sx={moneyCellSx}>{formatR(li.lineVat)}</TableCell>
                    <TableCell sx={moneyCellSx}>{formatR(li.lineTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Stack spacing={0.5} sx={{ mt: 1.5, ml: "auto", width: { xs: "100%", sm: 300 } }}>
              <Row label="Subtotal" value={formatR(invoice.subtotal)} />
              <Row label="VAT" value={formatR(invoice.vatAmount)} />
              <Row label="Total" value={formatR(invoice.totalAmount)} strong />
              <Row label="Outstanding" value={formatR(invoice.outstandingAmount)} danger={(invoice.outstandingAmount ?? 0) > 0} />
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleDownloadPdf}>Download tax invoice (PDF)</Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Record proof of payment */}
            {invoice.status !== "PAID" ? (
              <>
                <Typography fontWeight={800} mb={1}>Record proof of payment</Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                  <TextField size="small" label="Amount (R)" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} sx={fieldSx} />
                  <TextField size="small" label="Paid date" type="date" InputLabelProps={{ shrink: true }} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} sx={fieldSx} />
                  <TextField size="small" label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} sx={fieldSx} />
                  <TextField size="small" label="Bank / cash account" value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)} sx={fieldSx} />
                  <Button component="label" variant="outlined" size="small" sx={{ height: 40 }}>
                    {file ? file.name.slice(0, 18) : "Attach PoP"}
                    <input hidden type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                  </Button>
                  <Button variant="contained" disabled={busy} startIcon={spinner("record")} onClick={handleRecord} sx={{ height: 40, ...ifasDisabledBtnSx }}>Record</Button>
                </Stack>
              </>
            ) : (
              <Typography fontWeight={700} color="#1A7F4B">Invoice fully paid.</Typography>
            )}

            {/* Payment history */}
            <Typography fontWeight={800} mt={2.5} mb={1}>Payments</Typography>
            {payments.length === 0 ? (
              <Typography fontSize={13} color="#6B7280">No payments recorded yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Paid Date</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p: any) => (
                    <TableRow key={p.paymentId}>
                      <TableCell sx={moneyCellSx}>{formatR(p.amount)}</TableCell>
                      <TableCell>{fmtDate(p.paidDate)}</TableCell>
                      <TableCell>{p.reference || "-"}</TableCell>
                      <TableCell><StatusChip status={p.status} /></TableCell>
                      <TableCell align="right">
                        {p.status === "UPLOADED" ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="contained" color="success" disabled={busy} sx={ifasDisabledBtnSx}
                              startIcon={spinner(`confirm:${p.paymentId}`)}
                              onClick={() => handleConfirm(p.paymentId)}>Confirm</Button>
                            <Button size="small" variant="outlined" color="error" disabled={busy} sx={ifasDisabledBtnSx}
                              startIcon={spinner(`reject:${p.paymentId}`)}
                              onClick={() => handleReject(p.paymentId)}>Reject</Button>
                          </Stack>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Acknowledgement of Debt — the only lawful release-without-payment path (FR-094/095) */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2.5} mb={1}>
              <Typography fontWeight={800}>Acknowledgement of Debt</Typography>
              {(invoice.outstandingAmount ?? 0) > 0 && !aodOpen ? (
                <Button size="small" variant="outlined" onClick={() => setAodOpen(true)}>
                  Issue AoD Request
                </Button>
              ) : null}
            </Stack>

            {aodOpen ? (
              <Box sx={{ p: 1.5, border: "1px solid #E5E7EB", borderRadius: 1, mb: 1.5 }}>
                <Typography fontSize={12} color="#6B7280" mb={1}>
                  Requires approval from both the GCOO and GCEO offices before the body may be
                  released without payment (SOP §12.1).
                </Typography>
                <TextField
                  fullWidth size="small" multiline minRows={2}
                  label="Reason for the Acknowledgement of Debt"
                  value={aodReason}
                  onChange={(e) => setAodReason(e.target.value)}
                />
                <Stack direction="row" spacing={1} mt={1.5} justifyContent="flex-end">
                  <Button size="small" onClick={() => { setAodOpen(false); setAodReason(""); }}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" disabled={busy} sx={ifasDisabledBtnSx} startIcon={spinner("aod")} onClick={askAodConfirmation}>
                    Submit for approval
                  </Button>
                </Stack>
              </Box>
            ) : null}

            {aods.length === 0 ? (
              <Typography fontSize={13} color="#6B7280">No AoD raised for this case.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>AoD</TableCell>
                    <TableCell sx={{ fontWeight: 800 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>GCOO</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>GCEO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aods.map((a: any) => (
                    <TableRow key={a.aodNumber}>
                      <TableCell>{a.aodNumber}</TableCell>
                      <TableCell sx={moneyCellSx}>{formatR(a.amount)}</TableCell>
                      <TableCell><StatusChip status={a.status} /></TableCell>
                      <TableCell>{a.gcooApprovedBy || "—"}</TableCell>
                      <TableCell>{a.gceoApprovedBy || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>

      <OperationsConfirmDialog
        open={aodConfirmOpen}
        title="Issue Acknowledgement of Debt request?"
        busy={busy}
        confirmLabel="Issue AoD request"
        message={
          <>
            This raises an AoD for <b>{formatR(invoice?.outstandingAmount)}</b> on case{" "}
            <b>{invoice?.caseNumber}</b> and starts the approval chain — it must be signed off by
            both the GCOO and the GCEO offices (SOP §12.1).
          </>
        }
        onCancel={() => setAodConfirmOpen(false)}
        onConfirm={submitAod}
      />
    </Dialog>
  );
}

function Row({ label, value, strong, danger }: { label: string; value: string; strong?: boolean; danger?: boolean }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography fontSize={14} fontWeight={strong ? 800 : 600} color="#6B7280">{label}</Typography>
      <Typography fontSize={14} fontWeight={strong ? 900 : 700} color={danger ? "#B3341F" : "#111827"} sx={moneyCellSx}>{value}</Typography>
    </Box>
  );
}
